import { ref, computed, onMounted } from 'vue'
import { defineStore } from 'pinia'
import axios from 'axios'
import { useUserStore } from './user'

export interface Notification {
  id: number
  title: string
  content: string
  is_read: number
  created_at: string
  updated_at: string
  type: string
  user_id: number | null
  avatar?: string
  username?: string
  target_id?: number
  target_type?: string
}

export const useNotificationStore = defineStore('notification', () => {
  const notifications = ref<Notification[]>([])
  const userStore = useUserStore()
  const isFetching = ref(false)
  const lastFetchTime = ref<Date | null>(null)
  
  // 计算未读通知数量
  const unreadCount = computed(() => {
    return notifications.value.filter(n => n.is_read === 0).length
  })

  // 获取通知列表
  const fetchNotifications = async () => {
    if (isFetching.value) {
      console.log('已有通知请求正在进行中，跳过此次请求')
      return
    }
    
    // 开始加载状态
    isFetching.value = true
    lastFetchTime.value = new Date()
    
    try {
      console.log('开始获取通知，时间:', lastFetchTime.value)
      
      // 从本地存储获取令牌
      const token = localStorage.getItem('token')
      
      if (!token) {
        console.warn('获取通知失败: 未找到认证令牌')
        // 添加一条本地测试通知
        addLocalTestNotification()
        return
      }
      
      // 修正请求URL为/api/notifications (与后端路由注册一致)
      const requestUrl = '/api/notifications'
      console.log('发送通知请求到:', requestUrl, '认证令牌:', token.substring(0, 15) + '...')
      
      // 发送请求，包含认证头
      const response = await axios.get(requestUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      console.log('通知请求成功，状态:', response.status)
      
      if (response.data && Array.isArray(response.data)) {
        console.log(`收到 ${response.data.length} 条通知`)
        notifications.value = response.data
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        console.log(`收到 ${response.data.data.length} 条分页通知`)
        notifications.value = response.data.data
      } else {
        console.warn('通知数据格式不是预期的数组:', response.data)
        
        // 如果收到空数组或数据格式不对，添加本地测试通知
        if (notifications.value.length === 0) {
          addLocalTestNotification()
        }
      }
    } catch (error) {
      console.error('获取通知失败:', error)
      
      // 在请求失败时添加一条本地测试通知
      addLocalTestNotification()
    } finally {
      isFetching.value = false
    }
  }

  // 本地测试通知 - 用于开发和调试
  const addLocalTestNotification = () => {
    console.log('添加本地测试通知')
    const testNotification: Notification = {
      id: -1,
      title: '系统通知 (本地测试)',
      content: '这是一条本地生成的测试通知。如果您看到此通知，说明系统通知功能正在调试中或API请求失败。',
      is_read: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      type: 'system',
      user_id: null,
      avatar: '/logo.png',
      username: '系统',
      target_type: 'all'
    }
    
    // 确保本地测试通知只添加一次
    if (!notifications.value.some(n => n.id === -1)) {
      notifications.value.unshift(testNotification)
    }
  }

  // 标记通知为已读
  const markAsRead = async (id: number) => {
    try {
      // 本地测试通知直接在前端标记为已读
      if (id === -1) {
        const notification = notifications.value.find(n => n.id === -1)
        if (notification) {
          notification.is_read = 1
        }
        return
      }
      
      // 从本地存储获取令牌
      const token = localStorage.getItem('token')
      
      if (!token) {
        console.warn('标记通知已读失败: 未找到认证令牌')
        return
      }
      
      // 修正请求URL路径为/api/notifications
      await axios.put(`/api/notifications/${id}/read`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      console.log(`通知 ${id} 已标记为已读`)
      
      // 更新本地状态
      const notification = notifications.value.find(n => n.id === id)
      if (notification) {
        notification.is_read = 1
      }
    } catch (error) {
      console.error(`标记通知 ${id} 为已读失败:`, error)
    }
  }

  // 初始化时获取通知
  onMounted(() => {
    if (userStore.isLoggedIn) {
      fetchNotifications()
    }
  })

  return {
    notifications,
    unreadCount,
    fetchNotifications,
    markAsRead,
    isFetching,
    lastFetchTime
  }
}) 